import { Button, Divider, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  accountsApi,
  campaignsApi,
  useGetCampaignsQuery,
  useGetCompaniesFiltersQuery,
} from "../api";
import { SelectedFilterList } from "../components/features/SelectedFilterList";
import { ChevronLeftIcon, PlusIcon } from "../components/images";
import {
  NoCampaignsBlock,
  NoResultFound,
  ProductListItem,
} from "../components/products";
import { CustomLoader } from "../components/ui/CustomLoader";
import { DropdownWithSearch } from "../components/ui/dropdown";
import { InputWithQuery } from "../components/ui/InputWithQuery";
import {
  useDebounceValue,
  useStateWithSearchParams,
  useAppDispatch,
  useAppSelector,
} from "../hooks";
import { AppRoutes, ButtonsType, CampaignPurpose } from "../types/enums";
import { QUERY_SEPARATOR } from "../constant";
import { clearUpdatedCompanies } from "../features/companySlice";
import { selectPermissions } from "../features/permissionsSlice";
import { DemoWrapper } from "../components/products/DemoWrapper";
import { ReuseButton } from "../components/ui";

type SearchParams = {
  search?: string;
  company?: string;
};

const ProductsPage = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isAllowedEditCreateNewCampaign } = useAppSelector(selectPermissions);

  const [isDemoCalaps, setIsDemoCalaps] = useState<boolean>(false);
  const { searchDict, setSearchValue } = useStateWithSearchParams<SearchParams>(
    {
      search: undefined,
      company: undefined,
    },
  );

  useEffect(() => {
    dispatch(accountsApi.util.resetApiState());
    dispatch(campaignsApi.util.resetApiState());
    dispatch(clearUpdatedCompanies());
  }, []);

  const searchValueDebounce = useDebounceValue(searchDict.search || "");
  const companiesValue = searchDict.company?.split(QUERY_SEPARATOR);

  const { data: companiesFilters } = useGetCompaniesFiltersQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const { data, isFetching } = useGetCampaignsQuery(
    {
      search: searchValueDebounce || undefined,
      companies: companiesValue,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  useEffect(() => {
    if (pathname === AppRoutes.HOME)
      navigate(AppRoutes.CAMPAIGNS, { replace: true });
  }, [pathname]);

  const companies = (companiesFilters?.items || []).map(({ name }) => name);

  const removeFilterHandler = (key: string, filter: string) => {
    const filters = searchDict[key as keyof SearchParams];
    if (!filters) return;

    setSearchValue(
      key as keyof SearchParams,
      filters
        .split(QUERY_SEPARATOR)
        .filter((el) => el !== filter)
        .join(QUERY_SEPARATOR),
    );
  };

  const isFilters = !!searchValueDebounce || !!companiesValue;
  const isNoData = isFilters && !isFetching && data?.length === 0;

  const demoCampaigns =
    data?.filter((p) =>
      [CampaignPurpose.demo, CampaignPurpose.sales].includes(
        p.campaign_purpose,
      ),
    ) || null;

  const isNoCampaigns =
    !isFetching &&
    ((data?.length === 1 && demoCampaigns?.length === 1) ||
      (data?.length === 2 && demoCampaigns?.length === 2));

  return (
    <Stack
      sx={{ px: { sm: 8, md: 8, lg: 10, xl: 20 }, pt: 4, pb: 6 }}
      gap="24px"
    >
      <Typography variant="h4" color="custom.textDarkBlue">
        Campaigns
      </Typography>
      <Stack direction="row" gap="8px">
        <InputWithQuery
          placeholder={"Search by campaign name, company, CID..."}
          maxWidth="528px"
          isDisabled={
            data &&
            data.length === 1 &&
            !Object.values(searchDict).some(Boolean)
          }
        />
        <DropdownWithSearch
          isDisabled={
            data &&
            data.length === 1 &&
            !Object.values(searchDict).some(Boolean)
          }
          placeholder="Company"
          query="company"
          items={companies}
        />
      </Stack>
      {searchDict.company && (
        <Stack direction="row" flexWrap="wrap" gap="8px">
          <SelectedFilterList
            filters={searchDict.company.split(QUERY_SEPARATOR) || []}
            label="Company"
            removeHandler={removeFilterHandler}
          />
        </Stack>
      )}

      {isFetching ? (
        <CustomLoader />
      ) : (
        <>
          {data && data.length > 0 && (
            <>
              {data.length === 2 && demoCampaigns?.length === 2 ? null : (
                <Stack gap="16px">
                  {data.map((product) => {
                    if (
                      [CampaignPurpose.demo, CampaignPurpose.sales].includes(
                        product.campaign_purpose,
                      )
                    )
                      return null;

                    return (
                      <ProductListItem key={product.id} product={product} />
                    );
                  })}

                  <Button
                    variant="contained"
                    fullWidth
                    sx={{
                      backgroundColor: "#FCFCFC",
                      boxShadow: "none",
                    }}
                    disabled={!isAllowedEditCreateNewCampaign}
                    onClick={() => navigate(AppRoutes.CREATE_CAMPAIGN)}
                  >
                    <PlusIcon color="#4C545B" width="32" height="32" />
                  </Button>
                  {demoCampaigns && (
                    <Stack
                      direction="row"
                      width="100%"
                      alignItems="center"
                      justifyContent="center"
                      flexWrap="nowrap"
                      paddingBottom="8px"
                    >
                      <Divider sx={{ flexGrow: 1 }} />

                      <ReuseButton
                        buttonType={ButtonsType.text}
                        onClick={() => setIsDemoCalaps(!isDemoCalaps)}
                        sx={{
                          backgroundColor: "#2194D2",
                          color: "#FFFFFF",
                          borderRadius: "32px",
                          fontSize: "14px",
                          padding: "6px 8px 6px 14px",
                          ":hover": {
                            background: "#1785be",
                          },
                        }}
                        endIcon={
                          <ChevronLeftIcon
                            rotate={isDemoCalaps ? "90" : "-90"}
                            color={"#FFFFFF"}
                          />
                        }
                      >
                        Explore Demo Campaign
                      </ReuseButton>

                      <Divider sx={{ flexGrow: 1 }} />
                    </Stack>
                  )}

                  {demoCampaigns && isDemoCalaps && (
                    <DemoWrapper items={demoCampaigns || []} />
                  )}
                </Stack>
              )}
            </>
          )}

          {isNoCampaigns && (
            <Stack gap="16px">
              <NoCampaignsBlock />
              <Stack paddingTop="16px" />
              <DemoWrapper items={demoCampaigns || []} />
              <Button
                variant="contained"
                fullWidth
                sx={{
                  backgroundColor: "#00000005",
                  boxShadow: "none",
                }}
                onClick={() => navigate(AppRoutes.CREATE_CAMPAIGN)}
                disabled={!isAllowedEditCreateNewCampaign}
              >
                <PlusIcon color="#4C545B" width="32" height="32" />
              </Button>
            </Stack>
          )}

          {isNoData && (
            <Stack flexGrow={1} justifyContent="center" alignItems="center">
              <NoResultFound />
            </Stack>
          )}
        </>
      )}
    </Stack>
  );
};

export default ProductsPage;
